import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { serviceConsumer } from "../../../network/ServiceConsumer";
import { ShowToastMessage } from "../../../helper/ShowToastMessage";
import { passwordValidation, repeatPwdValidation } from "../../../helper/Validations";

const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1050,
};

const cardStyle = {
    background: "#fff",
    borderRadius: "8px",
    padding: "24px",
    width: "420px",
    maxWidth: "92vw",
    boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
};

const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    border: "1px solid #ccc",
    borderRadius: "4px",
};

const labelStyle = { fontSize: "13px", color: "#555", marginBottom: "4px" };
const errStyle = { fontSize: "12px", color: "#b00020", marginTop: "2px" };

const cancelBtn = {
    padding: "6px 16px",
    border: "1px solid #ccc",
    background: "#fff",
    borderRadius: "4px",
    cursor: "pointer",
};

const saveBtn = {
    padding: "6px 16px",
    border: "none",
    background: "#68AD3F",
    color: "#fff",
    borderRadius: "4px",
    cursor: "pointer",
};

// Rotate the admin password from inside the panel (no email round-trip).
// On success the backend clears every issued session token, so anyone the
// password was shared with is signed out everywhere, including this session.
const ChangePasswordModal = ({ onClose }) => {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");

    const initialValues = { currentPassword: "", password: "", repeatPassword: "" };

    const validationSchema = Yup.object().shape({
        currentPassword: Yup.string().trim().required("This field is required"),
        password: passwordValidation,
        repeatPassword: repeatPwdValidation,
    });

    const submitHandler = async (values) => {
        try {
            setErr("");
            setSubmitting(true);
            const url = `${process.env.REACT_APP_BASE_URL}/api/admin/change-pw`;
            await serviceConsumer("POST", url, {
                currentPassword: values.currentPassword,
                newPassword: values.password,
            });
            ShowToastMessage("success", "Password updated. Please log in again.");
            localStorage.clear();
            navigate("/admin/login");
        } catch (error) {
            setErr(error?.messages || "Something went wrong");
            setSubmitting(false);
        }
    };

    return (
        <div style={overlayStyle} onClick={onClose}>
            <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
                <h4 style={{ marginBottom: "6px" }}>Change admin password</h4>
                <p style={{ fontSize: "13px", color: "#777" }}>
                    All signed-in sessions (including anyone the password was shared
                    with) will be logged out.
                </p>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={submitHandler}
                >
                    {({ isValid, dirty }) => (
                        <Form autoComplete="off">
                            <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "10px" }}>
                                <div>
                                    <div style={labelStyle}>Current password</div>
                                    <Field style={inputStyle} type="password" name="currentPassword" placeholder="Current password" />
                                    <ErrorMessage name="currentPassword" component="div" style={errStyle} />
                                </div>
                                <div>
                                    <div style={labelStyle}>New password</div>
                                    <Field style={inputStyle} type="password" name="password" placeholder="New password" />
                                    <ErrorMessage name="password" component="div" style={errStyle} />
                                </div>
                                <div>
                                    <div style={labelStyle}>Confirm new password</div>
                                    <Field style={inputStyle} type="password" name="repeatPassword" placeholder="Confirm new password" />
                                    <ErrorMessage name="repeatPassword" component="div" style={errStyle} />
                                </div>
                            </div>
                            {err && <div style={{ ...errStyle, marginTop: "10px" }}>{err}</div>}
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "18px" }}>
                                <button type="button" onClick={onClose} disabled={submitting} style={cancelBtn}>Cancel</button>
                                <button
                                    type="submit"
                                    disabled={submitting || !isValid || !dirty}
                                    style={{
                                        ...saveBtn,
                                        ...(submitting || !isValid || !dirty ? { opacity: 0.5, cursor: "not-allowed" } : {}),
                                    }}
                                >
                                    {submitting ? "Saving..." : "Update password"}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default ChangePasswordModal;
